import client, { mock, isRealApiEnabled } from "./client";
import ordersMock from "./mocks/orders";

/**
 * @endpoint GET /api/v1/orders
 */
export function list(params) {
  if (isRealApiEnabled()) return client.get("/v1/orders", { query: params });
  void params;
  return mock(ordersMock);
}

/**
 * @endpoint GET /api/v1/orders/:id
 */
export function get(id) {
  if (isRealApiEnabled())
    return client.get(`/v1/orders/${encodeURIComponent(id)}`);
  return mock(ordersMock.find((o) => o.id === id) || ordersMock[0]);
}

/**
 * @endpoint POST /api/v1/orders
 */
export function create(payload) {
  if (isRealApiEnabled()) return client.post("/v1/orders", payload);
  return mock({
    ...ordersMock[0],
    ...payload,
    id: `ord_new_${Date.now()}`,
    status: "pending",
  });
}

/**
 * @endpoint POST /api/v1/orders?preview=tax
 * Tax for a Create Order cart before it's placed. Body is the create payload;
 * nothing is created. Resolves { subtotalCents, taxCents, totalCents } — tax
 * and total are null when the BFF can't work them out (no address on file, SKU
 * with no tax record).
 */
export function estimateTax(payload) {
  if (isRealApiEnabled())
    return client.post("/v1/orders", payload, { query: { preview: "tax" } });
  void payload;
  return mock({
    subtotalCents: null,
    taxCents: null,
    totalCents: null,
    lines: [],
  });
}

/**
 * @endpoint POST /api/v1/orders/:id/status
 */
export function updateStatus(id, status) {
  if (isRealApiEnabled())
    return client.post(`/v1/orders/${encodeURIComponent(id)}/status`, {
      status,
    });
  const base = ordersMock.find((o) => o.id === id) || ordersMock[0];
  return mock({ ...base, status });
}

/**
 * @endpoint POST /api/v1/orders/:id/refund
 * Batched: body = { items: [{ itemId, quantity, amountCents }], reason,
 * shippingCents? } — shippingCents (shipping + its tax) only when the refund
 * takes the last items on the order.
 * Real API returns the updated order (with a persisted `auditTrail`).
 */
export function refund(id, { items, reason, shippingCents }) {
  if (isRealApiEnabled())
    return client.post(`/v1/orders/${encodeURIComponent(id)}/refund`, {
      items,
      reason,
      ...(shippingCents > 0 ? { shippingCents } : {}),
    });
  return mock({
    id,
    items,
    reason,
    actor: "support.user@wwg.local",
    occurredAt: new Date().toISOString(),
  });
}

/**
 * @endpoint POST /api/v1/orders/:id/cancel-item
 * Batched: body = { items: [{ itemId }], reason }.
 * Real API returns the updated order (with a persisted `auditTrail`).
 */
export function cancelItem(id, { items, reason }) {
  if (isRealApiEnabled())
    return client.post(`/v1/orders/${encodeURIComponent(id)}/cancel-item`, {
      items,
      reason,
    });
  return mock({
    id,
    items,
    reason,
    actor: "support.user@wwg.local",
    occurredAt: new Date().toISOString(),
  });
}

/**
 * @endpoint POST /api/v1/orders/:id/re-ship
 */
export function reShip(id, { itemId, reason }) {
  if (isRealApiEnabled())
    return client.post(`/v1/orders/${encodeURIComponent(id)}/re-ship`, {
      itemId,
      reason,
    });
  return mock({
    sourceOrderId: id,
    newOrderId: `ord_reship_${Date.now()}`,
    itemId,
    reason,
    actor: "support.user@wwg.local",
    occurredAt: new Date().toISOString(),
  });
}
